/**
 * Format an ISO-8601 timestamp (as sent by the API — UTC, e.g. `2024-03-29T10:00:00Z`) as a short, unambiguous date
 * ("29 Mar 2024"). Day-first + short month avoids the MM/DD ambiguity of numeric formats and reads cleanly next to the
 * English labels. Returns "" for a null/empty/invalid value (so a card can fall back gracefully). i18n (Paraglide) can
 * localize the format later.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(d);
}

/**
 * Like {@link formatDate} but with the time of day (24h) appended — "27 Jul 2026, 09:47". Used where the exact moment
 * matters (the manual audit blocks — owner request). Rendered in the viewer's local timezone (the API sends UTC).
 * Returns "" for a null/empty/invalid value.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

const RELATIVE_UNITS: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 31_536_000_000],
  ["month", 2_592_000_000],
  ["week", 604_800_000],
  ["day", 86_400_000],
  ["hour", 3_600_000],
  ["minute", 60_000],
];

/**
 * A coarse relative age — "3 days ago", "1 week ago" — for feed-style timestamps (the ratings list). `numeric: "always"`
 * keeps the number ("1 week ago", not "last week") to match the mockup. Under a minute reads "just now"; a null/empty/
 * invalid value → "". Computed against the viewer's clock (the API sends UTC ISO). i18n (Paraglide) can localize later.
 */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = d.getTime() - Date.now();
  if (Math.abs(diffMs) < 60_000) return "just now";
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "always" });
  for (const [unit, ms] of RELATIVE_UNITS) {
    if (Math.abs(diffMs) >= ms) return rtf.format(Math.round(diffMs / ms), unit);
  }
  return "just now";
}
